import { Alert, Badge, Button, Group, Stack, Text, Title } from '@mantine/core';
import { useCompositionRecord } from '@/hooks/api/automations/useExecutionCompositions.ts';
import PublicationPanel from '@/pages/Editor/components/Publication/PublicationPanel.tsx';
import type { CompositionRecord } from '@/types/executionCompositions.ts';
import CompositionEvidence from './CompositionEvidence.tsx';
import { SavedCandidateSummary } from './CompositionPresentation.tsx';

export function SavedReleaseReview({
  record,
  canEdit,
  onChanged,
}: {
  record: CompositionRecord;
  canEdit: boolean;
  onChanged?: () => void;
}) {
  const frozen = record.status === 'frozen';
  const publishable = frozen || record.status === 'promoted';
  const historical = ['rolled_back', 'superseded'].includes(record.status);
  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Title order={3}>Saved candidate review</Title>
          <Text size="sm" c="dimmed">
            {historical
              ? 'This candidate is historical. Inspect its exact record and publication outcome.'
              : record.status === 'promoted'
                ? 'This candidate has been published to at least one entrypoint. Review current activations before publishing any additional eligible entrypoints.'
                : frozen
                  ? 'This candidate is frozen. Review the publication plan before a human confirms it.'
                  : 'Resolve readiness and freeze the exact candidate before publication review.'}
          </Text>
        </Stack>
        <Badge color={historical ? 'gray' : publishable ? 'green' : 'orange'}>
          {record.status.replaceAll('_', ' ')}
        </Badge>
      </Group>
      <SavedCandidateSummary record={record} />
      <CompositionEvidence
        releaseId={record.record.release_id}
        manifest={record.candidate}
        lifecycleRevision={record.record.lifecycle_revision}
        identity={record.record.identity}
        canEdit={canEdit}
        status={record.status}
        onChanged={onChanged}
      />
      {publishable && (
        <>
          <Title order={3}>Publication review</Title>
          <PublicationPanel
            releaseId={record.record.release_id}
            canEdit={canEdit}
            showFreezeAction={false}
          />
        </>
      )}
    </Stack>
  );
}

export default function SavedReleaseWorkflow({
  releaseId,
  canEdit,
}: {
  releaseId: string;
  canEdit: boolean;
}) {
  const result = useCompositionRecord(releaseId);
  if (result.isError)
    return (
      <Alert color="red">
        Saved release configuration unavailable.
        <Button disabled={result.isFetching} onClick={() => void result.refetch()}>
          Retry saved release inspection
        </Button>
      </Alert>
    );
  if (!result.data || !result.isFetchedAfterMount)
    return <Text>Loading saved release configuration…</Text>;
  return (
    <SavedReleaseReview
      record={result.data}
      canEdit={canEdit}
      onChanged={() => void result.refetch()}
    />
  );
}
